import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

import { CodingModel, DiagnosisAuthorType, DiagnosisRole, codingOf, codingSystemOf } from '@lib/guarantees/data-access';
import { TranslatePipe } from '@ngx-translate/core';

import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';

import { ChipComponent } from './chip.component';
import { CODING_SYSTEM_LABEL_KEY, DIAGNOSIS_AUTHOR_HINT_KEY, DIAGNOSIS_ROLE_LABEL_KEY } from './guarantee-vocabulary';

/**
 * The shape both a grouped diagnosis and a stored one satisfy, so the chip renders either
 * without the caller converting between them.
 */
export interface DiagnosisSummary {
  role?: DiagnosisRole;
  icd10?: CodingModel;
  icpc2?: CodingModel;
  caseId?: string;
  authorType?: DiagnosisAuthorType;
  authorName?: string;
  isSuspended?: boolean;
  suspensionReason?: string;
}

/**
 * One diagnosis, read the same way everywhere it appears: role, code, name, who asserted it and
 * whether it has been suspended. A prescription can treat several, so this is deliberately
 * compact enough to sit in a row of them.
 */
@Component({
  selector: 'guarantees-diagnosis-chip',
  standalone: true,
  imports: [MatIcon, MatTooltip, TranslatePipe, ChipComponent],
  template: `
    <span class="inline-flex max-w-full min-w-0 items-center gap-1">
      <!--
        Named rather than only coloured: what a code is to the visit changes what is guaranteed
        against it, and a manager reading a care plan should not have to hover to tell the condition
        being treated from the ones it comes with.
      -->
      @if (diagnosis().role; as role) {
        <guarantees-chip
          [tone]="role === roles.Primary ? 'primary' : 'neutral'"
          [icon]="role === roles.Primary ? 'circle' : 'radio_button_unchecked'"
          [tooltip]="(role === roles.Primary ? 'guaranteeV2.message.primaryDiagnosisHint' : 'guaranteeV2.message.secondaryDiagnosisHint') | translate">
          {{ roleLabelKey[role] | translate }}
        </guarantees-chip>
      }

      <!--
        A health manager's diagnosis sits alongside the doctor's rather than replacing it, and says
        so before the code rather than after the name: who asserted it qualifies everything to its
        right. Who exactly is a hover away — a name in the row costs a column and settles nothing.
      -->
      @if (diagnosis().authorType && diagnosis().authorType !== authorTypes.Doctor) {
        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-400" [matTooltip]="(authorHintKey() | translate) + authorSuffix()">{{
          diagnosis().authorType === authorTypes.HealthManager ? 'badge' : 'smart_toy'
        }}</mat-icon>
      }

      <span class="shrink-0 font-mono text-sm font-semibold text-neutral-700" [matTooltip]="codingLabelKey() | translate">
        {{ coding()?.code || ('guaranteeV2.label.unknownDiagnosis' | translate) }}
      </span>

      @if (showName() && coding()?.name) {
        <span class="min-w-0 truncate text-sm" [class]="diagnosis().isSuspended ? 'text-neutral-400 line-through' : 'text-neutral-500'" [matTooltip]="coding().name">
          {{ coding().name }}
        </span>
      }

      <!-- Without a case the diagnosis is only grouped by nosology, which a manager may need to fix. -->
      @if (showCaseState() && !diagnosis().caseId) {
        <mat-icon class="h-3.5! w-3.5! shrink-0 text-sm! leading-3.5! text-neutral-300" [matTooltip]="'guaranteeV2.message.noCaseHint' | translate">folder_off</mat-icon>
      }

      @if (diagnosis().isSuspended) {
        <guarantees-chip
          tone="warning"
          icon="pause_circle"
          [tooltip]="
            diagnosis().suspensionReason
              ? ('guaranteeV2.message.diagnosisSuspendedHint' | translate: { reason: diagnosis().suspensionReason })
              : ('guaranteeV2.message.diagnosisSuspendedNoReasonHint' | translate)
          ">
          {{ 'guaranteeV2.label.diagnosisSuspended' | translate }}
        </guarantees-chip>
      }
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosisChipComponent {
  protected readonly roles = DiagnosisRole;
  protected readonly authorTypes = DiagnosisAuthorType;
  protected readonly roleLabelKey = DIAGNOSIS_ROLE_LABEL_KEY;

  diagnosis = input.required<DiagnosisSummary>();
  /** Off in tight rows where the code alone identifies it. */
  showName = input<boolean>(true);
  /** Off where the case is already stated by the surrounding block. */
  showCaseState = input<boolean>(true);

  protected readonly coding = computed(() => codingOf(this.diagnosis()));

  /** Says which book the code is from: an ICPC-2 code read as ICD-10 names a different condition. */
  protected readonly codingLabelKey = computed(() => {
    const system = codingSystemOf(this.diagnosis());

    return system ? CODING_SYSTEM_LABEL_KEY[system] : CODING_SYSTEM_LABEL_KEY.icd10;
  });

  protected readonly authorHintKey = computed(() => {
    const authorType = this.diagnosis().authorType;

    return authorType ? (DIAGNOSIS_AUTHOR_HINT_KEY[authorType] ?? '') : '';
  });

  /** Who exactly, where the record names them. Appended to the hint rather than given its own cell. */
  protected readonly authorSuffix = computed(() => {
    const name = this.diagnosis().authorName?.trim();

    return name ? `: ${name}` : '';
  });
}
